import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { studentsApi, Student, StudentStatus } from "../students/api";
import { useAuth } from "../../../contexts/AuthContext";
import { useMasterData } from "../../../hooks/useMasterData";

const RteStudentsList: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { masters, fetchMasters, fetchSections: fetchSectionsHook } =
    useMasterData();

  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    classId: "",
    sectionId: "",
    status: "",
  });

  const [sections, setSections] = useState<any[]>([]);

  useEffect(() => {
    fetchMasters();
  }, [fetchMasters]);

  useEffect(() => {
    fetchStudents();
  }, [page, limit, filters, search]);

  useEffect(() => {
    if (filters.classId) {
      (async () => {
        const secs = await fetchSectionsHook(filters.classId);
        setSections(secs);
      })();
    } else {
      setSections([]);
    }
  }, [filters.classId, fetchSectionsHook]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await studentsApi.getAll({
        page: page + 1,
        limit,
        search,
        classId: filters.classId || undefined,
        sectionId: filters.sectionId || undefined,
        status: filters.status || undefined,
        rte: true,
      });
      setStudents(response.data);
      setTotal(response.meta.total);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportCsv = async () => {
    if (!hasPermission("RTE_STUDENTS_VIEW")) {
      return;
    }
    if (total === 0) {
      return;
    }
    setExporting(true);
    try {
      const response = await studentsApi.getAll({
        page: 1,
        limit: total,
        search,
        classId: filters.classId || undefined,
        sectionId: filters.sectionId || undefined,
        status: filters.status || undefined,
        rte: true,
      });
      const rows: Student[] = response.data || [];
      const header = [
        "Admission No",
        "Student Name",
        "Father Name",
        "Class",
        "Section",
        "Mobile",
        "Gender",
      ];
      const lines = [header.join(",")];
      rows.forEach((s) => {
        const admissionNumber = s.admissionNumber || "";
        const name = `${s.firstName} ${s.lastName || ""}`.trim();
        const fatherName = s.parent?.fatherName || "";
        const className = s.class?.name || "";
        const sectionName = s.section?.name || "";
        const mobile = s.phone || s.parent?.fatherMobile || "";
        const gender = s.gender || "";
        const values = [
          admissionNumber,
          name,
          fatherName,
          className,
          sectionName,
          mobile,
          gender,
        ].map((v) => {
          const value = v || "";
          if (value.includes(",") || value.includes('"')) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        });
        lines.push(values.join(","));
      });
      const csvContent = lines.join("\r\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "rte-students.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
    } finally {
      setExporting(false);
    }
  };

  const classChartData = useMemo(() => {
    const counts: Record<string, number> = {};
    students.forEach((s) => {
      const key = s.class?.name || "Unknown";
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.keys(counts).map((className) => ({
      className,
      students: counts[className],
    }));
  }, [students]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case StudentStatus.ACTIVE:
        return "success";
      case StudentStatus.INACTIVE:
        return "error";
      case StudentStatus.PASSED_OUT:
        return "info";
      case StudentStatus.TC:
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          RTE Students
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Students marked as RTE in the Scholar Register
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6">RTE Student List</Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportCsv}
            disabled={exporting || loading || total === 0}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by Name, Adm No..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Class"
              value={filters.classId}
              onChange={(e) => handleFilterChange("classId", e.target.value)}
            >
              <MenuItem value="">All Classes</MenuItem>
              {(masters.classes || []).map((cls) => (
                <MenuItem key={cls.id} value={cls.id}>
                  {cls.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Section"
              value={filters.sectionId}
              onChange={(e) => handleFilterChange("sectionId", e.target.value)}
              disabled={!filters.classId}
            >
              <MenuItem value="">All Sections</MenuItem>
              {sections.map((sec) => (
                <MenuItem key={sec.id} value={sec.id}>
                  {sec.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              {Object.values(StudentStatus).map((status) => (
                <MenuItem key={status} value={status}>
                  {status.replace("_", " ")}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Admission No</TableCell>
                    <TableCell>Student</TableCell>
                    <TableCell>Father</TableCell>
                    <TableCell>Class / Section</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : students.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No RTE students found
                      </TableCell>
                    </TableRow>
                  ) : (
                    students.map((student) => (
                      <TableRow key={student.id}>
                        <TableCell>{student.admissionNumber}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {student.firstName} {student.lastName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            SR: {student.scholarNumber || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {student.parent?.fatherName || "-"}
                        </TableCell>
                        <TableCell>
                          {student.class?.name} - {student.section?.name}
                        </TableCell>
                        <TableCell>
                          {student.phone ||
                            student.parent?.fatherMobile ||
                            "-"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={student.status}
                            size="small"
                            color={getStatusColor(student.status)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() =>
                              navigate(
                                `/scholar-register/students/${student.id}`,
                              )
                            }
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={limit}
              onRowsPerPageChange={handleLimitChange}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="subtitle1" gutterBottom>
              Class Wise RTE Students
            </Typography>
            <Box sx={{ height: 260 }}>
              {classChartData.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No data available.
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={classChartData}>
                    <XAxis dataKey="className" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="students" fill="#42a5f5" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default RteStudentsList;

